import { useEffect, type ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import '@/realtime/bannerBindings';
import '@/realtime/liveBindings';
import '@/realtime/chatLiveBindings';
import { startCallTabSync } from '@/realtime/callBindings';
import { electLeader } from '@/realtime/leader';
import { startMessagingTabSync } from '@/realtime/messagingBindings';
import { connectSocketsToSession } from '@/realtime/sockets';
import { startRinger } from '@/calls/ringer';
import { startCallLinkHandling } from '@/push/callLink';
import { startAccountLanguageSync } from '@/i18n/accountLanguage';
import { startPush } from '@/push/push';
import { useBannerStore } from '@/state/bannerStore';
import { resetCall } from '@/state/callStore';
import { startPreferencesSync } from '@/state/preferencesStore';
import { registerSignOutCleanup, startSessionSync, useSessionStore } from '@/state/sessionStore';
import { useTypingStore } from '@/state/typingStore';
import { startWarningsSync, useWarningsStore } from '@/state/warningsStore';
import { startTelemetry, telemetry } from '@/telemetry/telemetry';
// Imported directly, not from the primitives index, so the other primitives (and Base UI's menus,
// selects and popovers) stay out of the first download (tasks.md T204).
import { ToastProvider } from '@/ui/primitives/Toast';
import { queryClient } from './queryClient';

/**
 * App-wide services (tasks.md T058): the query cache, toasts, the session
 * (bootstrapped from the cookie, kept in step across tabs), the theme, the
 * realtime sockets and telemetry — each started once and stopped on unmount.
 */
function useAppServices() {
  const signedIn = useSessionStore((s) => s.status === 'signedIn');

  useEffect(() => {
    const stops = [
      startSessionSync(),
      startPreferencesSync(),
      connectSocketsToSession(queryClient),
      startMessagingTabSync(queryClient),
      startCallTabSync(),
      registerSignOutCleanup(() => {
        resetCall();
        queryClient.clear();
        useBannerStore.getState().clear();
        useTypingStore.getState().clear();
        useWarningsStore.getState().clear();
        void telemetry.reset();
      }),
    ];
    void useSessionStore.getState().bootstrap();
    return () => stops.forEach((stop) => stop());
  }, []);

  useEffect(() => (signedIn ? startTelemetry() : undefined), [signedIn]);

  // FR-044: an administrator's warning reaches the member within a minute.
  useEffect(() => (signedIn ? startWarningsSync() : undefined), [signedIn]);

  // Browser push (FR-036): registers when already allowed; removed again before sign-out.
  useEffect(() => (signedIn ? startPush() : undefined), [signedIn]);

  // The language follows the account: a new browser starts in it, and a change here is saved.
  useEffect(() => (signedIn ? startAccountLanguageSync() : undefined), [signedIn]);

  // A call answered or opened from its push notification rings in this tab.
  useEffect(() => (signedIn ? startCallLinkHandling() : undefined), [signedIn]);

  // One tab per browser rings (WEB-011): the leader, for as long as it is open.
  useEffect(() => {
    if (!signedIn) return undefined;
    const leadership = electLeader();
    const stopRinger = startRinger({ isLeader: leadership.isLeader });
    return () => {
      stopRinger();
      leadership.release();
    };
  }, [signedIn]);
}

export function Providers({ children }: { children: ReactNode }) {
  useAppServices();
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>{children}</ToastProvider>
    </QueryClientProvider>
  );
}
